import { TidyStorage } from './../../shared/providers/tidy-storage';
import { MatDialog } from '@angular/material/dialog';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { ThumbtackProvider } from 'src/providers/thumbtack/thumbtack.provider';
import { ProModel, ThumbtackProModel } from 'src/models/thumbtack.model';
import { NextStepsModalComponent } from './next-steps-modal-component/next-steps-modal-component';
import { ModalController } from '@ionic/angular';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Client } from 'src/providers/client/client';
import { Pros } from 'src/providers/pros/pros';
import { WindowService } from 'src/shared/providers/window.service';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'browse-pros.html',
    styleUrls: ['./browse-pros.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class BrowseProsPage implements OnInit {

  pros: ProModel[] = [];
  skeletons = Array(5).fill(0);
  wasDataLoaded = false;
  wasError = false;
  categoryHeader: string;
  isRightSideContent = true;
  customBack: string;
  propertyItems: any;
  serviceTypeItems: any;
  form: UntypedFormGroup;
  address: any;

  constructor(
    private navCtrl: CustomNavController,
    private thumbtackProvider: ThumbtackProvider,
    private modalCtrl: ModalController,
    private dialog: MatDialog,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private fb: UntypedFormBuilder,
    private client: Client,
    private prosService: Pros,
    private windowService: WindowService,
  ) {
    this.form = this.fb.group({
      address: [''],
      serviceType: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogPageTitle(new TranslationPipe().transform('Browse Pros'));
      }
      this.loadPropertyItems();
      this.loadServiceTypeItems();
      const dialogParams = await this.storage.retrieve('dialog-params');
      const address = dialogParams?.address || await this.navCtrl.getParam('address');
      this.form.patchValue({
        address: address.zip,
        serviceType: 'Cleaning',
      });
      await this.getThumbtackPros();
      this.rightSidePanelService.setDialogLoading(false);
    } catch (error) {
      this.wasError = true;
    }
  }

  async loadPropertyItems() {
    const addresses = await this.client.getMoreDetailAddresses();
    this.propertyItems = [];
    addresses.map((address) => {
      this.propertyItems.push({
        viewValue: address.address_name ? address.address_name : address.address1,
        value: address.zip
      });
    });
  }

  async loadServiceTypeItems() {
    const categories = await this.prosService.getServiceCategories();
    this.serviceTypeItems = categories.map((category) => ({
        icon: category.icon_url,
        viewValue: new TranslationPipe().transform(category.name === 'Regular Cleaning' ? 'Cleaning' : category.name),
        value: category.name === 'Regular Cleaning' ? 'Cleaning' : category.name,
      }));
  }

  async getThumbtackPros(): Promise<void> {
    try {
      this.wasDataLoaded = false;
      this.wasError = false;
      const thumbtackCategory = this.thumbtackProvider.getThumbtackCategory(this.form.value.serviceType);
      const thumbtackParams = {
        zipCode: this.form.value.address,
        categoryName: thumbtackCategory?.categoryName,
        categoryId: thumbtackCategory?.categoryId,
      };
      const response = await this.thumbtackProvider.getProList(thumbtackParams);
      this.pros = this.parsePros(response.results);
      this.wasDataLoaded = true;
      if (!this.pros.length) {
        this.wasError = true;
      }
    } catch (error) {
      this.wasError = true;
    }
  }

  parsePros(pros: ThumbtackProModel[]): ProModel[] {
    return pros.map((pro: ThumbtackProModel) => ({
        serviceId: pro.service_id,
        businessName: pro.business_name,
        rating: pro.rating,
        numReviews: pro.num_reviews,
        yearsInBusiness: pro.years_in_business,
        numHires: pro.num_hires,
        thumbtackUrl: pro.thumbtack_url,
        imageUrl: pro.image_url,
        backgroundImageUrl: pro.background_image_url,
        featuredReview: pro.featured_review,
        introduction: pro.introduction,
        location: pro.location,
        numOfEmployees: pro.num_of_employees,
        hasBackgroundCheck: pro.has_background_check,
        businessLocation: pro.business_location,
        iframeUrl: pro.iframe_url,
        pills: pro.pills,
      }));
  }

  async openNextStepsModal(pro: ProModel): Promise<void> {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
        const dialog = this.dialog.open(NextStepsModalComponent, {
          panelClass: 'next-steps-modal',
        })
        dialog.afterClosed().subscribe((res) => {
          if (res) {
            this.goToThumbtack(pro);
          }
        })
      } else {
        this.modalCtrl.create({
          component: NextStepsModalComponent,
          mode: 'ios',
          backdropDismiss: true,
          showBackdrop: true,
          breakpoints: [0, 0.3, 0.5, 0.8, 1],
          initialBreakpoint: 0.8,
          presentingElement: await this.modalCtrl.getTop(),
          canDismiss: true,
        }).then(modal => {
          modal.present();
          modal.onDidDismiss().then((res) => {
            if (res?.data) {
              this.goToThumbtack(pro);
            }
          })
        });
      }
  }

  goToThumbtack(pro: ProModel): void {
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url: pro.thumbtackUrl });
      return;
    }
    window.open(pro.thumbtackUrl, '_blank');
  }
}
